/**
 * «Дзвінки без результату» (Г2-W1; calls.md, розд. 4; рішення Архітектора 22.10): мої дзвінки за 7
 * днів без записаного результату — запитом списку `POST /api/v1/records/<дзвінок>/query` з якорем
 * часткового індексу поведінки `no_outcome` (`owner_id`, `created_at`; умова — `call_outcome_at`
 * порожнє). Серверної секції в `/activities/today` немає: модуль задач дзвінків не читає.
 *
 * - `owner eq me` — у самому дереві фільтра: `viewId` сервер фільтром не застосовує.
 * - Лише завершені й `tel:` (`phase = ended` або `source = tel`; Г2-4): дзвінок телефонії, що ще
 *   дзвонить чи триває, у секцію не потрапляє. Обидва поля `filterable`; умова — у корені поруч із
 *   якорем, тож якір індексу лишається.
 *
 * Файл першого екрана («Сьогодні»): лише форма запиту, без React і zod.
 */
import type { FilterNode, RecordPage } from '@clientline/contracts';
import { api, platformKeys } from '../../platform/api.ts';

/**
 * Ключ кешу — під списками сутності дзвінка: подія SSE дзвінка (`calls.*`, `record.*`) позначає його
 * застарілим так само, як списки (events.ts), а префікс `p` скидає вихід.
 */
export const noOutcomeKey = (entity: string) =>
  [...platformKeys.lists(entity), 'no-outcome'] as const;

/** Стільки рядків бере секція (найновіші). */
export const NO_OUTCOME_LIMIT = 20;
/** За скільки днів: «останні 7 днів» — сьогодні й 6 днів перед ним (filter-language.md, розд. 3). */
export const NO_OUTCOME_DAYS = 7;

/** Тіло запиту: якір індексу (`owner`, `call_outcome_at`), межа часу й порядок індексу. */
export function noOutcomeQuery() {
  const filter: FilterNode = {
    and: [
      { field: 'owner', op: 'eq', value: { token: 'me' } },
      { field: 'call_outcome_at', op: 'is_null' },
      { field: 'created_at', op: 'within', value: { token: 'last_n_days', n: NO_OUTCOME_DAYS } },
      {
        or: [
          { field: 'phase', op: 'eq', value: 'ended' },
          { field: 'source', op: 'eq', value: 'tel' },
        ],
      },
    ],
  };
  return {
    filter,
    sort: [{ field: 'created_at', dir: 'desc' as const }],
    limit: NO_OUTCOME_LIMIT,
  };
}

export const fetchNoOutcome = (entity: string, signal?: AbortSignal) =>
  api<RecordPage>('POST', `/api/v1/records/${entity}/query`, {
    body: noOutcomeQuery(),
    ...(signal ? { signal } : {}),
  });
